type RippleWebGlContext = WebGLRenderingContext & {
	_rtex?: WebGLTexture;
};

// rippleShader.ts  (copy-paste as-is)
export class RippleShader {
	canvas: HTMLCanvasElement;
	private gl: WebGLRenderingContext;
	private prog: WebGLProgram;
	private uTime: WebGLUniformLocation;
	private uRes: WebGLUniformLocation;
	private start = performance.now();

	// void main(){
	//     vec2 uv=v;
	//     float l=f(uv);
	//     vec4 c=texture2D(tex,uv);
	//     c.rgb*=1.0+l*0.25;      /* brighten/darken only */
	//     gl_FragColor=c;
	//   }

	constructor(w: number, h: number) {
		this.canvas = document.createElement('canvas');
		this.resize(w, h);
		this.gl = this.canvas.getContext('webgl')!;

		const vert = `
        attribute vec2 p;varying vec2 v;
        void main(){v=p*0.5+0.5;gl_Position=vec4(p,0.,1.);}
      `;
		const frag = `
        precision mediump float;
        uniform sampler2D tex;uniform float t;uniform vec2 r;varying vec2 v;
        float f(vec2 uv){vec2 a=vec2(r.x/r.y,1.);vec2 d=(uv-0.5)*a;
          float dist=max(length(d),0.02);
          return sin(50.0*dist-2.0*t)/(50.0*dist+1.0);
        }
        void main(){
          vec2 uv=v;
          float l=f(uv);
          vec4 c = texture2D(tex, uv);
          float lum = dot(c.rgb, vec3(0.299, 0.587, 0.114));   // brightness of pixel
          float amp = mix(0.15, 0.15, lum);                    // stronger on darks
          c.rgb = clamp(c.rgb + l * amp, 0.0, 1.0);            // additive ripple
          gl_FragColor = c;
        }
      `;
		const g = this.gl as RippleWebGlContext;
		const sh = (s: string, t: number): WebGLShader => {
			const h = g.createShader(t)!;
			g.shaderSource(h, s);
			g.compileShader(h);
			return h;
		};
		const p = g.createProgram()!;
		g.attachShader(p, sh(vert, g.VERTEX_SHADER));
		g.attachShader(p, sh(frag, g.FRAGMENT_SHADER));
		g.linkProgram(p);
		this.prog = p;
		g.useProgram(p);

		const buf = g.createBuffer()!;
		g.bindBuffer(g.ARRAY_BUFFER, buf);
		g.bufferData(
			g.ARRAY_BUFFER,
			new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
			g.STATIC_DRAW
		);
		const loc = g.getAttribLocation(p, 'p');
		g.enableVertexAttribArray(loc);
		g.vertexAttribPointer(loc, 2, g.FLOAT, false, 0, 0);

		this.uTime = g.getUniformLocation(p, 't')!;
		this.uRes = g.getUniformLocation(p, 'r')!;
	}

	resize(w: number, h: number) {
		this.canvas.width = w;
		this.canvas.height = h;
	}

	draw(source: HTMLCanvasElement) {
		const g = this.gl as RippleWebGlContext;
		g.viewport(0, 0, this.canvas.width, this.canvas.height);

		/* upload/update texture */
		let tex = g._rtex;
		if (!tex) {
			tex = g.createTexture()!;
			g._rtex = tex;
			g.bindTexture(g.TEXTURE_2D, tex);
			g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_S, g.CLAMP_TO_EDGE);
			g.texParameteri(g.TEXTURE_2D, g.TEXTURE_WRAP_T, g.CLAMP_TO_EDGE);
			g.texParameteri(g.TEXTURE_2D, g.TEXTURE_MIN_FILTER, g.LINEAR);
		}
		g.bindTexture(g.TEXTURE_2D, tex);
		g.pixelStorei(g.UNPACK_FLIP_Y_WEBGL, true); // <-- fixes upside-down upload
		g.texImage2D(g.TEXTURE_2D, 0, g.RGBA, g.RGBA, g.UNSIGNED_BYTE, source);

		/* uniforms */
		g.uniform1f(this.uTime, (performance.now() - this.start) / 1000);
		g.uniform2f(this.uRes, this.canvas.width, this.canvas.height);

		/* draw */
		g.drawArrays(g.TRIANGLES, 0, 6);
	}
}
